import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowBack,
  Sensors,
  BugReport,
  RestartAlt,
  Settings,
  ExpandMore,
  Warning,
  CheckCircle,
  Error,
  PlayArrow,
  Stop,
  Refresh,
  Save,
  Info,
  Wifi,
  WifiOff,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Tooltip,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  TextField,
  LinearProgress,
  IconButton,
} from "@mui/material";
import BreadCrumb from "../Components/BreadCrumb";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useNotification } from "../Context/notificationContext";
import { useKitchenSocket } from "../Hooks/useKitchenSocket";
import { KITCHEN_API_URL } from "../constants";
import { motion, AnimatePresence } from "framer-motion";

// GraphQL Queries and Mutations
const GET_USER_SENSOR_SETTINGS = gql`
  query GetUserSensorSettings {
    getUserSensorSettings {
      id
      userId
      debugMode
      enableSignalCheck
      enableUtensilCheck
      enableDispenserCheck
      created_at
      updated_at
      sensors {
        id
        name
        position
        isEnabled
        isSkipped
        status
        signal
        error
        lastChecked
      }
    }
  }
`;

const UPDATE_USER_SENSOR_SETTINGS = gql`
  mutation UpdateUserSensorSettings($input: UpdateSensorSettingsInput!) {
    updateUserSensorSettings(input: $input) {
      id
      userId
      debugMode
      enableSignalCheck
      enableUtensilCheck
      enableDispenserCheck
      created_at
      updated_at
      sensors {
        id
        name
        position
        isEnabled
        isSkipped
        status
        signal
        error
        lastChecked
      }
    }
  }
`;

const RESET_SENSOR_SETTINGS_TO_DEFAULT = gql`
  mutation ResetSensorSettingsToDefault {
    resetSensorSettingsToDefault {
      id
      userId
      debugMode
      enableSignalCheck
      enableUtensilCheck
      enableDispenserCheck
      created_at
      updated_at
      sensors {
        id
        name
        position
        isEnabled
        isSkipped
        status
        signal
        error
        lastChecked
      }
    }
  }
`;

const UPDATE_SENSOR_STATUS = gql`
  mutation UpdateSensorStatus($input: UpdateSensorStatusInput!) {
    updateSensorStatus(input: $input)
  }
`;

const SettingsPage = () => {
  const theme = useTheme();
  // Kitchen socket for real-time sensor checking
  const { isConnected, checkSignals, connectionError } =
    useKitchenSocket(KITCHEN_API_URL);

  // State management
  const [sensorSettings, setSensorSettings] = useState({
    debugMode: false,
    enableSignalCheck: true,
    enableUtensilCheck: true,
    enableDispenserCheck: true,
    sensors: [],
  });
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [checkingStatus, setCheckingStatus] = useState({
    isChecking: false,
    progress: 0,
    currentSensor: null,
    results: {},
    totalSensors: 0,
    checkedSensors: 0,
  });

  // GraphQL hooks
  const {
    loading: sensorLoading,
    error: sensorError,
    data: sensorData,
    refetch: refetchSensorSettings,
  } = useQuery(GET_USER_SENSOR_SETTINGS, {
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "no-cache",
  });

  const [updateSensorSettings, { loading: updatingSensorSettings }] =
    useMutation(UPDATE_USER_SENSOR_SETTINGS, {
      onCompleted: (data) => {
        console.log("🔧 Sensor settings mutation completed with data:", data);
        showNotificationMessage(
          "success",
          "Sensor settings updated successfully"
        );
        refetchSensorSettings();
      },
      onError: (error) => {
        console.error("🔧 Sensor settings mutation failed:", error);
        showNotificationMessage(
          "error",
          `Sensor settings update failed: ${error.message}`
        );
      },
    });

  const [resetSensorSettings, { loading: resettingSensorSettings }] =
    useMutation(RESET_SENSOR_SETTINGS_TO_DEFAULT, {
      onCompleted: () => {
        showNotificationMessage("success", "Sensor settings reset to default");
        refetchSensorSettings();
      },
      onError: (error) => {
        showNotificationMessage(
          "error",
          `Failed to reset sensor settings: ${error.message}`
        );
      },
    });

  const [updateSensorStatus] = useMutation(UPDATE_SENSOR_STATUS);

  // Initialize sensor settings
  useEffect(() => {
    if (sensorData?.getUserSensorSettings) {
      const settings = sensorData.getUserSensorSettings;
      console.log("🔧 Initializing settings from GraphQL:", settings);

      setSensorSettings({
        debugMode: settings.debugMode || false,
        enableSignalCheck: settings.enableSignalCheck !== false, // Default to true if undefined
        enableUtensilCheck: settings.enableUtensilCheck !== false, // Default to true if undefined
        enableDispenserCheck: settings.enableDispenserCheck !== false, // Default to true if undefined
        sensors: settings.sensors || [],
      });

      console.log("🔧 Settings initialized:", {
        debugMode: settings.debugMode || false,
        enableSignalCheck: settings.enableSignalCheck !== false,
        enableUtensilCheck: settings.enableUtensilCheck !== false,
        enableDispenserCheck: settings.enableDispenserCheck !== false,
        sensorsCount: (settings.sensors || []).length,
      });
    }
  }, [sensorData]);

  // Notification handler
  const showNotificationMessage = useCallback(
    (severity, message, duration = 5000) => {
      setNotification({
        open: true,
        message,
        severity,
      });
      setTimeout(() => {
        setNotification((prev) => ({ ...prev, open: false }));
      }, duration);
    },
    []
  );

  // Sensor settings handlers
  const handleSensorToggle = (sensorId, field) => {
    setSensorSettings((prev) => ({
      ...prev,
      sensors: prev.sensors.map((sensor) =>
        sensor.id === sensorId ? { ...sensor, [field]: !sensor[field] } : sensor
      ),
    }));
  };

  const handleSensorPositionChange = (sensorId, newPosition) => {
    setSensorSettings((prev) => ({
      ...prev,
      sensors: prev.sensors.map((sensor) =>
        sensor.id === sensorId
          ? { ...sensor, position: parseInt(newPosition) }
          : sensor
      ),
    }));
  };

  const handleDebugModeToggle = () => {
    setSensorSettings((prev) => ({
      ...prev,
      debugMode: !prev.debugMode,
    }));
  };

  const handleSignalCheckToggle = () => {
    console.log(
      "🔧 Signal check toggle clicked, current state:",
      sensorSettings.enableSignalCheck
    );
    setSensorSettings((prev) => {
      const newValue = !prev.enableSignalCheck;
      console.log("🔧 Setting enableSignalCheck to:", newValue);
      return {
        ...prev,
        enableSignalCheck: newValue,
      };
    });
  };

  const handleUtensilCheckToggle = () => {
    console.log(
      "🔧 Utensil check toggle clicked, current state:",
      sensorSettings.enableUtensilCheck
    );
    setSensorSettings((prev) => {
      const newValue = !prev.enableUtensilCheck;
      console.log("🔧 Setting enableUtensilCheck to:", newValue);
      return {
        ...prev,
        enableUtensilCheck: newValue,
      };
    });
  };

  const handleDispenserCheckToggle = () => {
    console.log(
      "🔧 Dispenser check toggle clicked, current state:",
      sensorSettings.enableDispenserCheck
    );
    setSensorSettings((prev) => {
      const newValue = !prev.enableDispenserCheck;
      console.log("🔧 Setting enableDispenserCheck to:", newValue);
      return {
        ...prev,
        enableDispenserCheck: newValue,
      };
    });
  };

  const handleSensorNameChange = (sensorId, newName) => {
    setSensorSettings((prev) => ({
      ...prev,
      sensors: prev.sensors.map((sensor) =>
        sensor.id === sensorId ? { ...sensor, name: newName } : sensor
      ),
    }));
  };

  const handleSaveSensorSettings = async () => {
    try {
      const inputData = {
        debugMode: sensorSettings.debugMode,
        enableSignalCheck: sensorSettings.enableSignalCheck,
        enableUtensilCheck: sensorSettings.enableUtensilCheck,
        enableDispenserCheck: sensorSettings.enableDispenserCheck,
        sensors: sensorSettings.sensors.map((sensor) => ({
          name: sensor.name,
          position: sensor.position,
          isEnabled: sensor.isEnabled,
          isSkipped: sensor.isSkipped,
        })),
      };

      console.log("🔧 Saving sensor settings with data:", inputData);

      await updateSensorSettings({
        variables: {
          input: inputData,
        },
      });
    } catch (error) {
      console.error("Failed to save sensor settings:", error);
    }
  };

  const handleResetSensorSettings = async () => {
    if (
      window.confirm(
        "Are you sure you want to reset all sensor settings to default? This action cannot be undone."
      )
    ) {
      try {
        await resetSensorSettings();
      } catch (error) {
        console.error("Failed to reset sensor settings:", error);
      }
    }
  };

  const handleCheckAllSensors = async () => {
    if (!isConnected) {
      showNotificationMessage(
        "error",
        "Kitchen not connected. Please check your connection."
      );
      return;
    }

    const enabledSensors = sensorSettings.sensors.filter(
      (sensor) => sensor.isEnabled && !sensor.isSkipped
    );

    if (enabledSensors.length === 0) {
      showNotificationMessage("warning", "No enabled sensors to check");
      return;
    }

    setCheckingStatus({
      isChecking: true,
      progress: 0,
      currentSensor: null,
      results: {},
      totalSensors: enabledSensors.length,
      checkedSensors: 0,
    });

    try {
      showNotificationMessage(
        "info",
        `Starting sensor check for ${enabledSensors.length} sensors...`
      );

      // Call checkSignals which returns array of 30 signals
      console.log("🔍 Calling checkSignals() for fresh data...");
      const signalsResponse = await checkSignals();
      console.log("📡 Fresh signals response received:", signalsResponse);
      console.log("📊 Response type:", typeof signalsResponse);
      console.log(
        "📋 Response keys:",
        signalsResponse ? Object.keys(signalsResponse) : "null"
      );

      // Handle the actual response format from useKitchenSocket
      if (typeof signalsResponse === "object" && signalsResponse !== null) {
        // Response contains: { success, totalSignals, passedSignals, failedSensors, skippedSensors, rawSignals? }
        const detailedResults = {};
        let detailedCheckedCount = 0;

        // Process each enabled sensor using the detailed response
        for (const sensor of enabledSensors) {
          setCheckingStatus((prev) => ({
            ...prev,
            currentSensor: sensor,
            progress: (detailedCheckedCount / enabledSensors.length) * 100,
            checkedSensors: detailedCheckedCount,
          }));

          // Add artificial delay for better UX
          await new Promise((resolve) => setTimeout(resolve, 300));

          // Check if this sensor is in the failed sensors list
          const failedSensor = signalsResponse.failedSensors?.find(
            (fs) => fs.position === sensor.position
          );
          const skippedSensor = signalsResponse.skippedSensors?.find(
            (ss) => ss.position === sensor.position
          );

          let status,
            signal,
            error = null;

          if (skippedSensor) {
            // Sensor was skipped - should not happen for enabled sensors, but handle it
            status = "offline";
            signal = 0; // 0% signal
            error = "Sensor is disabled or skipped";
          } else if (failedSensor) {
            // Sensor failed (original signal value from hook was 0, meaning no signal)
            status = "offline";
            signal = 0; // 0% signal
            error = failedSensor.reason || "No signal detected";
          } else {
            // Sensor passed (original signal value from hook was 1, meaning good signal)
            status = "online";
            signal = 100; // 100% signal
          }

          const timestamp = new Date().toISOString();
          const result = {
            sensorId: sensor.id,
            position: sensor.position,
            name: sensor.name,
            status,
            signal, // Already 0 or 100
            timestamp,
            error,
          };

          detailedResults[sensor.id] = result;
          detailedCheckedCount++;

          // Update sensor status in backend
          try {
            console.log(
              `💾 Updating database for sensor ${sensor.name} (Position ${sensor.position}):`,
              {
                status,
                signal, // Already 0 or 100
                timestamp,
                error,
              }
            );

            await updateSensorStatus({
              variables: {
                input: {
                  position: sensor.position,
                  status,
                  signal, // Already 0 or 100
                  timestamp,
                  error,
                },
              },
            });

            console.log(
              `✅ Database updated successfully for sensor ${sensor.name}`
            );
          } catch (statusError) {
            console.error(
              `❌ Failed to update status for sensor ${sensor.name}:`,
              statusError
            );
          }

          // Update progress in real-time
          setCheckingStatus((prev) => ({
            ...prev,
            results: { ...prev.results, [sensor.id]: result },
            checkedSensors: detailedCheckedCount,
          }));
        }

        // Final update
        setCheckingStatus({
          isChecking: false,
          progress: 100,
          currentSensor: null,
          results: detailedResults,
          totalSensors: enabledSensors.length,
          checkedSensors: enabledSensors.length,
        });

        // Refetch sensor settings to get updated database status
        console.log(
          "🔄 Refetching sensor settings to get updated database status..."
        );
        await refetchSensorSettings();
        console.log("✅ Sensor settings refetched successfully");

        const detailedFailedSensors = Object.values(detailedResults).filter(
          (r) => r.status !== "online"
        );
        const detailedSuccessMessage =
          detailedFailedSensors.length === 0
            ? `All ${enabledSensors.length} sensors are online!`
            : `Sensor check completed. ${detailedFailedSensors.length} sensor(s) offline.`;

        showNotificationMessage(
          detailedFailedSensors.length === 0 ? "success" : "warning",
          detailedSuccessMessage
        );
      } else if (Array.isArray(signalsResponse)) {
        // Fallback: Handle direct array response (old format)
        const signalsArray = signalsResponse;

        if (signalsArray.length !== 30) {
          throw new Error("Expected array of 30 signals");
        }

        const arrayResults = {};
        let arrayCheckedCount = 0;

        // Check each enabled sensor one by one with visual progress
        for (const sensor of enabledSensors) {
          setCheckingStatus((prev) => ({
            ...prev,
            currentSensor: sensor,
            progress: (arrayCheckedCount / enabledSensors.length) * 100,
            checkedSensors: arrayCheckedCount,
          }));

          // Add artificial delay for better UX
          await new Promise((resolve) => setTimeout(resolve, 300));

          // MODIFIED: Interpret signalValue: 1 as good (100%), 0 as bad (0%)
          const signalValueFromHook = signalsArray[sensor.position - 1]; // Convert 1-based to 0-based index
          const isGoodSignal = signalValueFromHook === 1;
          const status = isGoodSignal ? "online" : "offline";
          const signalPercentage = isGoodSignal ? 100 : 0;
          const timestamp = new Date().toISOString();

          const result = {
            sensorId: sensor.id,
            position: sensor.position,
            name: sensor.name,
            status,
            signal: signalPercentage,
            timestamp,
            error: status === "offline" ? "No signal detected" : null,
          };

          arrayResults[sensor.id] = result;
          arrayCheckedCount++;

          // Update sensor status in backend
          try {
            console.log(
              `💾 Updating database for sensor ${sensor.name} (Position ${sensor.position}):`,
              {
                status,
                signal: signalPercentage,
                timestamp,
                error: result.error,
              }
            );

            await updateSensorStatus({
              variables: {
                input: {
                  position: sensor.position,
                  status,
                  signal: signalPercentage,
                  timestamp,
                  error: result.error,
                },
              },
            });

            console.log(
              `✅ Database updated successfully for sensor ${sensor.name}`
            );
          } catch (statusError) {
            console.error(
              `❌ Failed to update status for sensor ${sensor.name}:`,
              statusError
            );
          }

          // Update progress in real-time
          setCheckingStatus((prev) => ({
            ...prev,
            results: { ...prev.results, [sensor.id]: result },
            checkedSensors: arrayCheckedCount,
          }));
        }

        // Final update
        setCheckingStatus({
          isChecking: false,
          progress: 100,
          currentSensor: null,
          results: arrayResults,
          totalSensors: enabledSensors.length,
          checkedSensors: enabledSensors.length,
        });

        // Refetch sensor settings to get updated database status
        console.log(
          "🔄 Refetching sensor settings to get updated database status..."
        );
        await refetchSensorSettings();
        console.log("✅ Sensor settings refetched successfully");

        const arrayFailedSensors = Object.values(arrayResults).filter(
          (r) => r.status !== "online"
        );
        const arraySuccessMessage =
          arrayFailedSensors.length === 0
            ? `All ${enabledSensors.length} sensors are online!`
            : `Sensor check completed. ${arrayFailedSensors.length} sensor(s) offline.`;

        showNotificationMessage(
          arrayFailedSensors.length === 0 ? "success" : "warning",
          arraySuccessMessage
        );
      } else {
        throw new Error("Invalid signals response format");
      }
    } catch (error) {
      console.error("Failed to check sensors:", error);
      setCheckingStatus((prev) => ({
        ...prev,
        isChecking: false,
      }));
      showNotificationMessage("error", `Sensor check failed: ${error.message}`);
    }
  };

  const getSensorStatusColor = (status) => {
    switch (status) {
      case "online":
        return "success";
      case "warning":
        return "warning";
      case "offline":
        return "error";
      case "error":
        return "error";
      default:
        return "default";
    }
  };

  const getSensorStatusIcon = (status) => {
    switch (status) {
      case "online":
        return <CheckCircle color="success" />;
      case "warning":
        return <Warning color="warning" />;
      case "offline":
      case "error":
        return <Error color="error" />;
      default:
        return <Sensors color="primary" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <BreadCrumb
        links={[{ label: "Dashboard", href: "/dashboard" }]}
        currentPage="Settings"
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            minHeight: "calc(100vh - 200px)",
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              p: 4,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.1)",
              }}
            />

            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                  <Settings sx={{ mr: 2, fontSize: "inherit" }} />
                  System Settings
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
                  Configure your kitchen sensors, system preferences, and
                  monitoring settings
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  display="flex"
                  gap={2}
                  justifyContent={{ xs: "center", md: "flex-end" }}
                >
                  {!isConnected ? (
                    <Button
                      variant="contained"
                      startIcon={<Wifi />}
                      onClick={() => {
                        showNotificationMessage(
                          "info",
                          "Attempting to reconnect to kitchen..."
                        );
                        // You could add reconnection logic here
                      }}
                      disabled={sensorLoading}
                      sx={{
                        background: "linear-gradient(45deg, #ff9800, #f57c00)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                        },
                      }}
                    >
                      Check Kitchen
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={
                        checkingStatus.isChecking ? <Stop /> : <PlayArrow />
                      }
                      onClick={
                        checkingStatus.isChecking
                          ? () => {}
                          : handleCheckAllSensors
                      }
                      disabled={checkingStatus.isChecking || sensorLoading}
                      sx={{
                        background: checkingStatus.isChecking
                          ? "linear-gradient(45deg, #f44336, #d32f2f)"
                          : "linear-gradient(45deg, #4caf50, #388e3c)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                        },
                      }}
                    >
                      {checkingStatus.isChecking
                        ? `Checking... (${checkingStatus.checkedSensors}/${checkingStatus.totalSensors})`
                        : "Check All Sensors"}
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>

            {/* Progress bar for sensor checking */}
            {checkingStatus.isChecking && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {checkingStatus.currentSensor
                    ? `Checking ${checkingStatus.currentSensor.name} (Position ${checkingStatus.currentSensor.position})...`
                    : "Initializing sensor check..."}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={checkingStatus.progress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "rgba(255,255,255,0.2)",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "white",
                      transition: "transform 0.3s ease",
                    },
                  }}
                />
                <Typography variant="caption" sx={{ mt: 1, opacity: 0.9 }}>
                  {checkingStatus.checkedSensors} of{" "}
                  {checkingStatus.totalSensors} sensors checked
                </Typography>
              </Box>
            )}
          </Box>

          {/* Main Content */}
          <Box sx={{ p: 4 }}>
            <Grid container spacing={4}>
              {/* Sensor Settings Card */}
              <Grid item xs={12} lg={8}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                    border: "1px solid rgba(102, 126, 234, 0.1)",
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 3,
                      }}
                    >
                      <Sensors sx={{ color: "#ff5722", fontSize: 32 }} />
                      <Typography variant="h6" fontWeight={600}>
                        Sensor Configuration
                      </Typography>
                    </Box>

                    {/* Debug Mode Toggle */}
                    <Box sx={{ mb: 4 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={sensorSettings.debugMode}
                            onChange={handleDebugModeToggle}
                            color="warning"
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <BugReport sx={{ color: "#ff9800" }} />
                            <Typography variant="subtitle1" fontWeight={600}>
                              Developer Debug Mode
                            </Typography>
                          </Box>
                        }
                        sx={{ mb: 2 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Enable position field editing for developers
                      </Typography>
                    </Box>

                    {/* Kitchen Check Settings */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ mb: 2, color: "#2e7d32" }}>
                        Kitchen Check Settings
                      </Typography>

                      {/* Debug Info */}
                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: "grey.100",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Debug: enableSignalCheck ={" "}
                          {String(sensorSettings.enableSignalCheck)},
                          enableUtensilCheck ={" "}
                          {String(sensorSettings.enableUtensilCheck)},
                          enableDispenserCheck ={" "}
                          {String(sensorSettings.enableDispenserCheck)}
                        </Typography>
                      </Box>

                      <Stack spacing={2}>
                        {/* Signal Check Toggle */}
                        <FormControlLabel
                          control={
                            <Switch
                              checked={sensorSettings.enableSignalCheck}
                              onChange={handleSignalCheckToggle}
                              color="success"
                            />
                          }
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Sensors sx={{ color: "#4caf50" }} />
                              <Typography variant="subtitle1" fontWeight={600}>
                                Enable Signal Check
                              </Typography>
                            </Box>
                          }
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 4 }}
                        >
                          Check all kitchen sensors before cooking starts
                        </Typography>

                        {/* Utensil Check Toggle */}
                        <FormControlLabel
                          control={
                            <Switch
                              checked={sensorSettings.enableUtensilCheck}
                              onChange={handleUtensilCheckToggle}
                              color="primary"
                            />
                          }
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Settings sx={{ color: "#2196f3" }} />
                              <Typography variant="subtitle1" fontWeight={600}>
                                Enable Utensil Check
                              </Typography>
                            </Box>
                          }
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 4 }}
                        >
                          Verify cooking pots are properly placed before cooking
                        </Typography>

                        {/* Dispenser Check Toggle */}
                        <FormControlLabel
                          control={
                            <Switch
                              checked={sensorSettings.enableDispenserCheck}
                              onChange={handleDispenserCheckToggle}
                              color="warning"
                            />
                          }
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Sensors sx={{ color: "#ff9800" }} />
                              <Typography variant="subtitle1" fontWeight={600}>
                                Enable Dispenser Check
                              </Typography>
                            </Box>
                          }
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 4 }}
                        >
                          Test grain and masala dispensers before cooking starts
                        </Typography>
                      </Stack>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Sensor List */}
                    {sensorLoading ? (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <CircularProgress size={40} />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 2 }}
                        >
                          Loading sensor settings...
                        </Typography>
                      </Box>
                    ) : (
                      <Stack
                        spacing={2}
                        sx={{ maxHeight: "500px", overflowY: "auto" }}
                      >
                        {sensorSettings.sensors.map((sensor) => (
                          <Accordion
                            key={sensor.id}
                            sx={{
                              bgcolor: "background.default",
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 2,
                              "&:before": { display: "none" },
                              "&.Mui-expanded": {
                                margin: 0,
                              },
                            }}
                            // disabled={sensor.position > 21}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMore />}
                              sx={{
                                minHeight: 64,
                                "&.Mui-expanded": { minHeight: 64 },
                                opacity: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  width: "100%",
                                  pr: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <Chip
                                    label={sensor.position}
                                    size="small"
                                    color={
                                      sensor.isEnabled ? "primary" : "default"
                                    }
                                    sx={{ minWidth: 40, fontWeight: 600 }}
                                  />
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                  >
                                    {sensor.name}
                                  </Typography>
                                  {/* {sensor.position > 21 && (
                                    <Chip
                                      label="Reserved"
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                    />
                                  )} */}
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  {/* Real-time checking indicator */}
                                  {checkingStatus.isChecking &&
                                    checkingStatus.currentSensor?.id ===
                                      sensor.id && (
                                      <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{
                                          duration: 1,
                                          repeat: Infinity,
                                          ease: "linear",
                                        }}
                                      >
                                        <CircularProgress
                                          size={16}
                                          color="primary"
                                        />
                                      </motion.div>
                                    )}

                                  {/* Sensor Status from Check Results */}
                                  {checkingStatus.results[sensor.id] && (
                                    <motion.div
                                      initial={{ scale: 0, opacity: 0 }}
                                      animate={{ scale: 1, opacity: 1 }}
                                      transition={{ duration: 0.3 }}
                                    >
                                      <Tooltip
                                        title={`Latest Check - Status: ${
                                          checkingStatus.results[sensor.id]
                                            .status
                                        } | Signal: ${
                                          checkingStatus.results[sensor.id]
                                            .signal
                                        }%`}
                                      >
                                        <Chip
                                          icon={getSensorStatusIcon(
                                            checkingStatus.results[sensor.id]
                                              .status
                                          )}
                                          label={
                                            checkingStatus.results[sensor.id]
                                              .status +
                                            ` (${
                                              checkingStatus.results[sensor.id]
                                                .signal
                                            }%)`
                                          }
                                          color={getSensorStatusColor(
                                            checkingStatus.results[sensor.id]
                                              .status
                                          )}
                                          size="small"
                                          variant="outlined"
                                        />
                                      </Tooltip>
                                    </motion.div>
                                  )}

                                  {/* Database Status Indicator - Only show if no recent check results */}
                                  {!checkingStatus.results[sensor.id] &&
                                    sensor.status &&
                                    sensor.status !== "unknown" && (
                                      <Tooltip
                                        title={`Stored Status: ${
                                          sensor.status
                                        } | Signal: ${
                                          typeof sensor.signal === "number"
                                            ? sensor.signal
                                            : 0
                                        }% ${
                                          sensor.lastChecked &&
                                          !isNaN(
                                            new Date(
                                              sensor.lastChecked
                                            ).getTime()
                                          )
                                            ? `| Last: ${new Date(
                                                sensor.lastChecked
                                              ).toLocaleDateString()}`
                                            : ""
                                        }`}
                                      >
                                        <Chip
                                          icon={getSensorStatusIcon(
                                            sensor.status
                                          )}
                                          label={`${sensor.status} (${
                                            typeof sensor.signal === "number"
                                              ? sensor.signal
                                              : 0
                                          }%)`}
                                          color={getSensorStatusColor(
                                            sensor.status
                                          )}
                                          size="small"
                                          variant="filled"
                                          sx={{
                                            fontSize: "0.75rem",
                                            height: "24px",
                                          }}
                                        />
                                      </Tooltip>
                                    )}

                                  <Tooltip
                                    title={
                                      sensor.isEnabled ? "Enabled" : "Disabled"
                                    }
                                  >
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: "50%",
                                        bgcolor: sensor.isEnabled
                                          ? "success.main"
                                          : "error.main",
                                      }}
                                    />
                                  </Tooltip>
                                  {sensor.isSkipped && (
                                    <Tooltip title="Skipped in checks">
                                      <Warning
                                        sx={{
                                          color: "warning.main",
                                          fontSize: 16,
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </Box>
                            </AccordionSummary>

                            {/* {sensor.position <= 21 && ( */}
                            <AccordionDetails sx={{ pt: 0 }}>
                              <Stack spacing={3}>
                                {/* Sensor Name Field */}
                                <TextField
                                  label="Sensor Name"
                                  value={sensor.name}
                                  onChange={(e) =>
                                    handleSensorNameChange(
                                      sensor.id,
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                  helperText="Custom name for this sensor"
                                  fullWidth
                                />

                                {/* Position Field (Debug Mode Only) */}
                                {sensorSettings.debugMode && (
                                  <TextField
                                    label="Position"
                                    type="number"
                                    value={sensor.position}
                                    onChange={(e) =>
                                      handleSensorPositionChange(
                                        sensor.id,
                                        e.target.value
                                      )
                                    }
                                    inputProps={{ min: 1, max: 30 }}
                                    size="small"
                                    helperText="Array position (1-30) for signal mapping"
                                  />
                                )}

                                {/* Current Database Status Display */}
                                {(sensor.status !== "unknown" ||
                                  sensor.lastChecked) && (
                                  <Alert
                                    severity={getSensorStatusColor(
                                      sensor.status
                                    )}
                                    sx={{ borderRadius: 2 }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight={600}
                                      sx={{ mb: 1 }}
                                    >
                                      Current Status (Stored):
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Typography variant="body2">
                                        <strong>Status:</strong>{" "}
                                        {sensor.status || "unknown"}
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>Signal:</strong>{" "}
                                        {typeof sensor.signal === "number"
                                          ? sensor.signal
                                          : 0}
                                        %
                                      </Typography>
                                      {sensor.error && (
                                        <Typography
                                          variant="body2"
                                          color="error"
                                        >
                                          <strong>Error:</strong> {sensor.error}
                                        </Typography>
                                      )}
                                      {sensor.lastChecked && (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          <strong>Last Checked:</strong>{" "}
                                          {sensor.lastChecked &&
                                          !isNaN(
                                            new Date(
                                              sensor.lastChecked
                                            ).getTime()
                                          )
                                            ? new Date(
                                                sensor.lastChecked
                                              ).toLocaleString()
                                            : "Invalid date"}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Alert>
                                )}

                                {/* Real-time Check Results Display */}
                                {checkingStatus.results[sensor.id] && (
                                  <Alert
                                    severity={getSensorStatusColor(
                                      checkingStatus.results[sensor.id].status
                                    )}
                                    sx={{ borderRadius: 2 }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight={600}
                                    >
                                      Latest Check Result:
                                    </Typography>
                                    <Typography variant="body2">
                                      Status:{" "}
                                      {checkingStatus.results[sensor.id].status}{" "}
                                      | Signal:{" "}
                                      {checkingStatus.results[sensor.id].signal}
                                      % | Time:{" "}
                                      {new Date(
                                        checkingStatus.results[
                                          sensor.id
                                        ].timestamp
                                      ).toLocaleTimeString()}
                                    </Typography>
                                  </Alert>
                                )}

                                {/* Enable/Disable Toggle */}
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={sensor.isEnabled}
                                      onChange={() =>
                                        handleSensorToggle(
                                          sensor.id,
                                          "isEnabled"
                                        )
                                      }
                                      color="success"
                                    />
                                  }
                                  label="Enable Sensor"
                                />

                                {/* Skip in Checks Toggle */}
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={sensor.isSkipped}
                                      onChange={() =>
                                        handleSensorToggle(
                                          sensor.id,
                                          "isSkipped"
                                        )
                                      }
                                      color="warning"
                                    />
                                  }
                                  label="Skip in Signal Checks"
                                />

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mt: 1 }}
                                >
                                  {sensor.isSkipped
                                    ? "This sensor will be ignored during signal checking"
                                    : sensor.isEnabled
                                    ? "This sensor will be checked for proper signals"
                                    : "This sensor is disabled"}
                                </Typography>
                              </Stack>
                            </AccordionDetails>
                            {/* )} */}
                          </Accordion>
                        ))}
                      </Stack>
                    )}

                    {sensorError && (
                      <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                        Failed to load sensor settings: {sensorError.message}
                      </Alert>
                    )}

                    {/* Action Buttons */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        mt: 4,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        onClick={handleResetSensorSettings}
                        variant="outlined"
                        color="warning"
                        startIcon={<RestartAlt />}
                        disabled={
                          resettingSensorSettings || updatingSensorSettings
                        }
                      >
                        {resettingSensorSettings ? (
                          <CircularProgress size={20} />
                        ) : (
                          "Reset to Default"
                        )}
                      </Button>

                      <Button
                        onClick={handleSaveSensorSettings}
                        variant="contained"
                        startIcon={<Save />}
                        disabled={
                          updatingSensorSettings || resettingSensorSettings
                        }
                      >
                        {updatingSensorSettings ? (
                          <CircularProgress size={20} />
                        ) : (
                          "Save Settings"
                        )}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* System Information Card */}
              <Grid item xs={12} lg={4}>
                <Card
                  sx={{
                    borderRadius: 3,
                    background: theme.palette.background.paper,
                    border: "1px solid #2196f330",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 3,
                      }}
                    >
                      <Info sx={{ color: "#2196f3", fontSize: 28 }} />
                      <Typography variant="h6" fontWeight={600} color="#1976d2">
                        System Information
                      </Typography>
                    </Box>

                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Kitchen Connection
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              bgcolor: isConnected
                                ? "success.main"
                                : "error.main",
                            }}
                          />
                          <Typography
                            variant="h6"
                            color="text.primary"
                            fontWeight={600}
                          >
                            {isConnected ? "Connected" : "Disconnected"}
                          </Typography>
                        </Box>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Sensors
                        </Typography>
                        <Typography
                          variant="h6"
                          color="text.primary"
                          fontWeight={600}
                        >
                          {sensorSettings.sensors.length}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Active Sensors
                        </Typography>
                        <Typography
                          variant="h6"
                          color="text.primary"
                          fontWeight={600}
                        >
                          {
                            sensorSettings.sensors.filter(
                              (s) => s.isEnabled && !s.isSkipped
                            ).length
                          }
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Inactive Sensors
                        </Typography>
                        <Typography
                          variant="h6"
                          color="text.primary"
                          fontWeight={600}
                        >
                          {
                            sensorSettings.sensors.filter(
                              (s) => !s.isEnabled || s.isSkipped
                            ).length
                          }
                        </Typography>
                      </Box>

                      {/* Database Status Summary */}
                      <Divider sx={{ my: 1 }} />
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Stored Status Summary
                        </Typography>
                        <Stack spacing={1}>
                          {(() => {
                            const statusCounts = sensorSettings.sensors.reduce(
                              (acc, sensor) => {
                                const status = sensor.status || "unknown";
                                acc[status] = (acc[status] || 0) + 1;
                                return acc;
                              },
                              {}
                            );

                            return Object.entries(statusCounts).map(
                              ([status, count]) => (
                                <Box
                                  key={status}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    {getSensorStatusIcon(status)}
                                    <Typography
                                      variant="caption"
                                      color="text.primary"
                                      sx={{ textTransform: "capitalize" }}
                                    >
                                      {status}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    color="text.primary"
                                  >
                                    {count}
                                  </Typography>
                                </Box>
                              )
                            );
                          })()}
                        </Stack>
                      </Box>

                      {/* Progress bar for sensor checking */}
                      {checkingStatus.isChecking && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {checkingStatus.currentSensor
                              ? `Checking ${checkingStatus.currentSensor.name} (Position ${checkingStatus.currentSensor.position})...`
                              : "Initializing sensor check..."}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={checkingStatus.progress}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: "rgba(255,255,255,0.2)",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: "white",
                                transition: "transform 0.3s ease",
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ mt: 1, opacity: 0.9 }}
                          >
                            {checkingStatus.checkedSensors} of{" "}
                            {checkingStatus.totalSensors} sensors checked
                          </Typography>
                        </Box>
                      )}

                      {Object.keys(checkingStatus.results).length > 0 && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            Last Check Results
                          </Typography>
                          <Stack
                            spacing={1}
                            sx={{ maxHeight: "200px", overflowY: "auto" }}
                          >
                            {Object.values(checkingStatus.results).map(
                              (result) => (
                                <Box
                                  key={result.sensorId}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    title={result.name}
                                  >
                                    {result.name.length > 15
                                      ? `${result.name.substring(0, 15)}...`
                                      : result.name}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {result.signal}%
                                    </Typography>
                                    <Chip
                                      label={result.status}
                                      size="small"
                                      color={getSensorStatusColor(
                                        result.status
                                      )}
                                      variant="outlined"
                                      sx={{
                                        minWidth: 60,
                                        height: 20,
                                        fontSize: "0.65rem",
                                      }}
                                    />
                                  </Box>
                                </Box>
                              )
                            )}
                          </Stack>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            Last checked:{" "}
                            {Object.values(checkingStatus.results)[0]?.timestamp
                              ? new Date(
                                  Object.values(
                                    checkingStatus.results
                                  )[0].timestamp
                                ).toLocaleTimeString()
                              : "Never"}
                          </Typography>
                        </Box>
                      )}

                      <Divider />

                      <Typography variant="body2" color="text.secondary">
                        Use the "Check All Sensors" button to test connectivity
                        and get real-time status updates for all enabled
                        sensors. Each sensor is checked individually via socket
                        connection and results are automatically saved to the
                        backend.
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </motion.div>

      {/* Enhanced Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 2,
            fontWeight: 500,
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
